import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { ENV_CONFIG } from '@ps/core-config';
import { withLoading } from '@ps/core-loading';
import { TemplateFolder } from '@ps/survey-utils';

import { TemplateFolderMapper } from './mapper/template.mapper';
import { TemplateFolderPayloadMapper } from './mapper/template.payload.mapper';
import {
  SaveTemplatePayloadInput,
  SaveTemplatePayloadMapper,
} from './mapper/save-template.payload.mapper';
import { TemplateFolderDto } from './dtos/template.dto';

export interface GetTemplatesParams {
  hasImpersonatedBuyer: boolean;
  companyId: number;
  /** Only folders the buyer may save into. Browsable != writable. */
  forTemplateDrawer?: boolean;
}

@Injectable({ providedIn: 'root' })
export class TemplateApi {
  private readonly _env = inject(ENV_CONFIG);
  private readonly _http = inject(HttpClient);

  private readonly _baseUrl = this._env.pureSpectrum.url;

  public getTemplates({
    hasImpersonatedBuyer,
    companyId,
    forTemplateDrawer = false,
  }: GetTemplatesParams): Observable<TemplateFolder[]> {
    const params = new HttpParams({
      fromObject: {
        UI: 1,
        is_service_buyer: hasImpersonatedBuyer,
        parent_company_id: companyId,
        self_owned: forTemplateDrawer,
        is_saving_template_op: forTemplateDrawer,
      },
    });
    return this._http
      .get<TemplateFolderDto[]>(`${this._baseUrl}/buyers/v2/templates`, {
        params,
        context: withLoading(forTemplateDrawer ? 'drawer' : 'page'),
      })
      .pipe(map((dtos) => new TemplateFolderMapper().fromDtoList(dtos)));
  }

  /**
   * `withLoading('page')` on both writes: the save-template drawer closes as it
   * emits its result and the override confirmation closes on confirm, so no
   * drawer is left on screen to host a spinner by the time either request fires.
   */
  public saveTemplate(input: SaveTemplatePayloadInput): Observable<void> {
    return this._http.post<void>(
      `${this._baseUrl}/buyers/v2/templates?UI=1`,
      new SaveTemplatePayloadMapper().toPayload(input),
      { context: withLoading('page') },
    );
  }

  public updateTemplate(
    templateId: number,
    input: SaveTemplatePayloadInput,
  ): Observable<void> {
    return this._http.put<void>(
      `${this._baseUrl}/buyers/v2/templates/${templateId}?UI=1`,
      new SaveTemplatePayloadMapper().toPayload(input),
      { context: withLoading('page') },
    );
  }

  public createTemplateFolder(
    name: string,
    hasImpersonatedBuyer: boolean,
    parentFolderId?: number,
  ): Observable<TemplateFolder> {
    const payload = new TemplateFolderPayloadMapper().toPayload({
      name,
      hasImpersonatedBuyer,
      parentFolderId,
    });
    return this._http
      .post<TemplateFolderDto>(
        `${this._baseUrl}/buyers/v2/templates/folders`,
        payload,
      )
      .pipe(map((dto) => new TemplateFolderMapper().fromDto(dto)));
  }
}
